import { faCompass } from '@fortawesome/pro-regular-svg-icons';
import { useLocation, useRouter } from '@tanstack/react-router';
import { useMetaData } from '@uturn/api/v1';
import { Button, Icon } from '@uturn/ui-kit';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/header';
import { SearchProvider } from '../components/search-provider';
import { getMainNavigation, getUserNavigation } from '../config/navigation';
import { useMountEffect } from '../hooks/useMountEffect';
import { translateLegacyRoute } from '../utils/translate-legacy-route';

export const NotFound = () => {
	const { t } = useTranslation();
	const router = useRouter();
	const pathname = useLocation({ select: (location) => location.pathname });
	const routeTranslation = translateLegacyRoute(pathname);

	const { data: metaData } = useMetaData({
		query: {
			refetchOnWindowFocus: false,
			select: (data) => data.data,
		},
	});

	const user = metaData?.user;

	useMountEffect(() => {
		if (routeTranslation !== null) {
			router.navigate({ to: routeTranslation, replace: true });
		}
	});

	const handleGoHome = () => {
		window.location.href = '/';
	};

	return (
		<>
			<Helmet title={t('pages.not-found.title', 'Not found').toString()} />
			<SearchProvider>
				<div className="h-full">
					{user && (
						<Header
							userNavigation={getMainNavigation(t)}
							dropdownItems={getUserNavigation(
								user?.organisation?.id as number,
								t,
							)}
							user={{
								name: `${user?.firstName} ${user?.surname}`,
								role: user?.role?.includes('ADMIN') ? 'Admin' : 'User',
							}}
						/>
					)}
					<div className="3xl:w-2/3 3xl:px-0 m-auto px-4 lg:px-8">
						<div className="flex min-h-[calc(100vh-10rem)] items-center justify-center">
							<div className="w-full max-w-md space-y-6 rounded-lg border bg-card p-8 shadow-lg">
								<div className="flex items-center justify-center">
									<div className="rounded-full bg-primary/10 p-3">
										<Icon className="h-8 w-8 text-primary" icon={faCompass} />
									</div>
								</div>

								<div className="space-y-2 text-center">
									<h1 className="text-2xl font-semibold tracking-tight">
										{t(
											'pages.not-found.heading',
											"Uh oh, we can't seem to find the page you're looking for.",
										)}
									</h1>
									<p className="text-sm text-muted-foreground">
										{t(
											'pages.not-found.description',
											'We have taken note of this, and will look into it. Contact us if you need help.',
										)}
									</p>
								</div>

								<div className="space-y-3">
									<Button onClick={handleGoHome} className="w-full" size="lg">
										{t('pages.not-found.button-label', 'Go home')}
									</Button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</SearchProvider>
		</>
	);
};
